import type { AvailableSite } from '@atlassian/gic-anywhere';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import {
	PERSISTED_KEYS_ON_SERVER,
	keys as localStorageKeys,
} from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import type { IssueType } from '../providers/BulkCreateContextProvider.tsx';
import type { JiraProject } from '../__types__/apiUtils.ts';

export enum ISSUE_CREATE_WITH_AI {
	enabled = 'enabled',
	disabled = 'disabled',
}

export const saveCloudIdToLocalStorage = (payload: { selectedCloudId: string }) => {
	if (payload?.selectedCloudId) {
		try {
			localStorage.setItem(
				PERSISTED_KEYS_ON_SERVER.PERSISTED_JIRA_CLOUD_ID_PREFERENCE,
				payload.selectedCloudId,
			);
		} catch (error: unknown) {
			throw new Error('Unable to save cloudId to localStorage');
		}
	}
};

export const getPersistedOverrideCloudId = () =>
	localStorage?.getItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_JIRA_CLOUD_ID_PREFERENCE);

export const clearPersistedOverrideCloudId = () => {
	try {
		localStorage?.removeItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_JIRA_CLOUD_ID_PREFERENCE);
	} catch (error: unknown) {
		throw new Error('Unable to clear persisted override cloudId');
	}
};

export const getIsCreateIssueWithAiEnabledByUser = () => {
	const isCreateIssueAiEnabled = localStorage?.getItem(localStorageKeys.ISSUE_CREATE_WITH_AI);
	// If the value is not set true should be returned by default
	return isCreateIssueAiEnabled !== ISSUE_CREATE_WITH_AI.disabled;
};

export const setCreateIssueWithAiEnabledByUser = (enabled: boolean) => {
	localStorage?.setItem(
		localStorageKeys.ISSUE_CREATE_WITH_AI,
		enabled ? ISSUE_CREATE_WITH_AI.enabled : ISSUE_CREATE_WITH_AI.disabled,
	);
};

export const saveJiraConfigDetailsToLocalStorage = ({
	selectedSite,
	selectedProject,
	selectedIssueType,
}: {
	selectedSite?: AvailableSite | null;
	selectedProject?: JiraProject | null;
	selectedIssueType?: IssueType | null;
}) => {
	try {
		if (selectedSite && selectedProject && selectedIssueType) {
			localStorage.setItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_BULK_CREATE_JIRA_CONFIG_FOR_USER, {
				selectedSite,
				selectedProject,
				selectedIssueType,
			});
		}
	} catch (error: unknown) {
		// eslint-disable-next-line no-console
		console.error(
			`Unable to set bulk create Jira config: ${error instanceof Error ? error.message : String(error)}`,
		);
	}
};

export const saveSingleWorkCreateJiraConfigToLocalStorage = ({
	selectedSite,
	selectedProject,
	selectedIssueType,
}: {
	selectedSite?: AvailableSite | null;
	selectedProject?: JiraProject | null;
	selectedIssueType?: IssueType | null;
}) => {
	try {
		if (selectedSite && selectedProject && selectedIssueType) {
			localStorage.setItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_SINGLE_CREATE_JIRA_CONFIG_FOR_USER, {
				selectedSite,
				selectedProject,
				selectedIssueType,
			});
		}
	} catch (error: unknown) {
		// eslint-disable-next-line no-console
		console.error(
			`Unable to set single work create Jira config: ${error instanceof Error ? error.message : String(error)}`,
		);
	}
};

export const getPersistedSingleWorkCreateJiraConfig = (): {
	selectedSite?: AvailableSite | null;
	selectedProject?: JiraProject | null;
	selectedIssueType?: IssueType | null;
} | null => {
	try {
		return localStorage?.getItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_SINGLE_CREATE_JIRA_CONFIG_FOR_USER,
		);
	} catch (error: unknown) {
		// eslint-disable-next-line no-console
		console.error(
			`Unable to get single work create Jira config: ${error instanceof Error ? error.message : String(error)}`,
		);
		return null;
	}
};

export const saveShouldUseEmbedToLocalStorage = (shouldUseEmbed: boolean) => {
	try {
		localStorage?.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_SHOULD_USE_GIC_EMBED,
			String(shouldUseEmbed),
		);
	} catch (error: unknown) {
		// eslint-disable-next-line no-console
		console.error(
			`Unable to save shouldUseEmbed to localStorage: ${error instanceof Error ? error.message : String(error)}`,
		);
	}
};

export const getPersistedShouldUseEmbed = (): boolean | null => {
	try {
		const value = localStorage?.getItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_SHOULD_USE_GIC_EMBED);
		if (value === null || value === undefined) {
			return null;
		}
		return value === 'true';
	} catch (error: unknown) {
		// eslint-disable-next-line no-console
		console.error(
			`Unable to get shouldUseEmbed from localStorage: ${error instanceof Error ? error.message : String(error)}`,
		);
		return null;
	}
};
